import React, { useState, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { styled } from '@mui/material/styles';
import { ErrorQuestionDto, SectionResponseDto } from 'models/dtoTypes';
import { TextFieldFormsy } from 'components/formsy';
import { get } from 'lodash';
import { getClaim, getFullName } from 'auth/account';
import { AxiosResponse } from 'axios';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import ToastAlert from 'components/ToastAlert';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import SmeConfigContext from 'data/SmeConfigContext';
import SmeConfig from 'data/SmeConfig';
import DialogTitle from '@mui/material/DialogTitle';
import { CreateClaim } from 'services/openapiClient';
import CurrentUser from 'auth/currentUser';

import Formsy from 'formsy-react';
import { useTranslation } from 'react-i18next';

const PREFIX = 'OpenClaimDialog';

const classes = {
  root: `${PREFIX}-root`,
  dialogContent: `${PREFIX}-dialogContent`,
  buttons: `${PREFIX}-buttons`,
  button: `${PREFIX}-button`,
  submitButton: `${PREFIX}-submitButton`,
  flashSpacer: `${PREFIX}-flashSpacer`,
  conditionalQuestion: `${PREFIX}-conditionalQuestion`,
  paper: `${PREFIX}-paper`,
  inputGroup: `${PREFIX}-inputGroup`,
};

const StyledSmeConfigContextConsumer = styled(SmeConfigContext.Consumer)(({ theme }) => ({
  [`& .${classes.root}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  [`& .${classes.dialogContent}`]: {
    minWidth: 500,
  },

  [`& .${classes.buttons}`]: {
    width: 'inherit',
    minWidth: 400,
    position: 'sticky',
    bottom: 0,
    right: 10,
    '& > *': {
      textAlign: 'center',
    },
  },

  [`& .${classes.button}`]: {
    margin: theme.spacing(1),
  },

  [`& .${classes.submitButton}`]: {
    minWidth: 100,
    height: 50,
  },

  [`& .${classes.flashSpacer}`]: {
    minHeight: 10,
  },

  [`& .${classes.conditionalQuestion}`]: {
    marginLeft: '30px',
    marginTop: '-20px',
  },

  [`& .${classes.paper}`]: {
    backgroundColor: theme.palette.background.paper,
    border: '2px solid #000',
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
  },

  [`& .${classes.inputGroup}`]: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),

    '& > *': {
      width: '100%',
    },
  },
}));

type HttpRequest = AxiosResponse<SectionResponseDto | ErrorQuestionDto>;

interface Attributes {
  [key: string]: string;
}

interface OnClose {
  (): void;
}

interface OnSave {
  (): void;
}

interface Props {
  open: boolean;
  onClose: OnClose;
  onSave?: OnSave;
}

interface Question {
  name: string;
  label: string;
  value?: string | number | boolean;
  editable: boolean;
}

function OpenClaimDialog(props: Props): JSX.Element {
  const { accounts } = useMsal();
  const formRef = useRef<Formsy>(null);
  const { open, onClose, onSave } = props;
  const [httpResponse, setHttpResponse] = useState<HttpRequest | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation('default');
  const handleClose = () => {
    setHttpResponse(undefined);
    onClose();
  };

  function getCurrentValues() {
    const formsyEl = formRef.current;
    return (formsyEl ? formsyEl.getCurrentValues() : {}) as Attributes;
  }

  async function handleCreate(e: React.FormEvent, config: SmeConfig) {
    e.stopPropagation();
    const { currentSectionResponse } = config;
    const responses = currentSectionResponse?.responses ?? [];
    const countryCodes =
      get(
        responses.find((r) => r.questionAlias === 'company-country'),
        'optionAliases',
      ) ?? [];
    const model = getCurrentValues();
    model.countryCode = countryCodes[0] ?? '';
    console.log('about to open new claim', model);
    setLoading(true);
    await CreateClaim(model);
    setLoading(false);
    onSave && onSave();
    await CurrentUser.getInstance().refresh(config);
  }

  const renderQuestionField = (field: Question) => {
    const { name, label, value, editable } = field;
    return (
      <div className={classes.inputGroup} key={name}>
        <TextFieldFormsy type="text" name={name} label={label} disabled={!editable} value={value} />
      </div>
    );
  };

  // nested form is bad, change to element 'div'
  const renderQuestions = (questions: Question[]) => (
    <Formsy formElement="div" ref={formRef} className="flex flex-col justify-center">
      {questions.map(renderQuestionField)}
    </Formsy>
  );

  const renderToastAlert = () => {
    if (!httpResponse) return <></>;
    const open = Boolean(httpResponse);
    const message =
      httpResponse.status === 200 ? 'Your answers have been saved' : 'The responses are not valid.';
    const severity = httpResponse.status === 200 ? 'success' : 'error';
    return (
      <ToastAlert
        open={open}
        severity={severity}
        message={message}
        onClose={() => setHttpResponse(undefined)}
      />
    );
  };

  return (
    <StyledSmeConfigContextConsumer>
      {(config: SmeConfig) => {
        const { currentSectionResponse } = config;
        const email = getClaim(accounts[0], 'email');
        const phoneNumber = getClaim(accounts[0], 'phoneNumber');
        const userName = getFullName(accounts[0]);
        const responses = currentSectionResponse?.responses ?? [];
        const companyName = get(
          responses.find((r) => r.questionAlias === 'company-name'),
          'value',
        );
        const companyCode = get(
          responses.find((r) => r.questionAlias === 'company-registration-number'),
          'value',
        );
        const questions = [
          {
            label: 'Company name',
            name: 'companyName',
            value: companyName,
            editable: !companyName,
          },
          {
            label: 'External Identifier',
            name: 'externalIdentifier',
            value: companyCode,
            editable: !companyCode,
          },
          {
            label: 'Contact name',
            name: 'contactName',
            value: userName,
            editable: !userName,
          },
          {
            label: 'Contact email',
            name: 'contactEmail',
            value: email,
            editable: !email,
          },
          {
            label: 'Contact telephone',
            name: 'phoneNumber',
            value: phoneNumber,
            editable: !phoneNumber,
          },
          { label: 'Additional comments', name: 'comment', editable: true },
        ];
        return (
          <Dialog
            open={open}
            onClose={handleClose}
            className={classes.root}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <Box boxShadow={2}>
              <DialogTitle>Open company claim</DialogTitle>
            </Box>
            <DialogContent className={classes.dialogContent}>
              <div>
                {loading && <CircularProgress />}
                {renderToastAlert()}
              </div>
              {renderQuestions(questions)}
            </DialogContent>
            <Box boxShadow={2}>
              <DialogActions>
                <Button onClick={handleClose} color="primary" className={classes.submitButton}>
                  {t('cancel')}
                </Button>
                <Button
                  color="primary"
                  autoFocus
                  onClick={(e: React.FormEvent) => handleCreate(e, config)}
                  className={classes.submitButton}
                  aria-label="Signup"
                >
                  {t('submit')}
                </Button>
              </DialogActions>
            </Box>
          </Dialog>
        );
      }}
    </StyledSmeConfigContextConsumer>
  );
}

export default OpenClaimDialog;
